/**
 * How an outreach job picks contacts from World-DB import lists.
 *
 * `importName` is the legacy single-list field. `importNames` is the current
 * multi-list field. Resolve prefers `importNames` when it is an array so old
 * jobs keep working.
 */

export const MANUAL_IMPORT = '__manual__';

/**
 * API-trigger pool. Contacts are neither pulled from lists nor added by hand —
 * they are enqueued one at a time by the outreach trigger endpoint
 * (`outreachApiTrigger`). Behaves like `manual` for every pool-scanning path
 * (no rescan, no list filter), but keeps its own label so the sidebar can show
 * the API-mode UI instead of the "add from table" hint.
 */
export const API_TRIGGER = '__api__';

/** Steps that can write an address onto a contact that starts without one. */
export const EMAIL_DISCOVERY_STEPS = new Set(['web_research', 'dm_email_finder']);

/**
 * A real mailbox is only required at Prepare / Send. Research may find it
 * first, so the contact pool must not demand `has_email` when these steps
 * run before that mail step.
 */
export const jobCanDiscoverEmail = (steps) =>
  Array.isArray(steps) && steps.some((s) => s && EMAIL_DISCOVERY_STEPS.has(s.type));

const asNameList = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => value[k]);
  }
  return null;
};

const cleanNames = (list) => {
  const rawList = asNameList(list);
  if (!rawList) return [];
  const out = [];
  const seen = new Set();
  for (const raw of rawList) {
    if (typeof raw !== 'string') continue;
    const name = raw.trim();
    if (!name || name === MANUAL_IMPORT || name === API_TRIGGER || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
};

export const resolvePoolLists = (pool = {}) => {
  const rawNames = asNameList(pool.importNames);
  if (rawNames) {
    // API mode wins over manual: if both markers are present (should not
    // happen, but defensive) we still route to API so the trigger endpoint
    // stays authoritative.
    const hasApi = rawNames.some((n) => n === API_TRIGGER);
    if (hasApi && cleanNames(rawNames).length === 0) {
      return { mode: 'api', names: [] };
    }
    const hasManual = rawNames.some((n) => n === MANUAL_IMPORT);
    if (hasManual && cleanNames(rawNames).length === 0) {
      return { mode: 'manual', names: [] };
    }
    const names = cleanNames(rawNames);
    if (names.length === 0) return { mode: 'any', names: [] };
    return { mode: 'lists', names };
  }

  const single = typeof pool.importName === 'string' ? pool.importName.trim() : '';
  if (single === API_TRIGGER) return { mode: 'api', names: [] };
  if (single === MANUAL_IMPORT) return { mode: 'manual', names: [] };
  if (!single) return { mode: 'any', names: [] };
  return { mode: 'lists', names: [single] };
};

export const applyPoolLists = (pool = {}, next = {}) => {
  if (next.mode === 'api') {
    // API jobs have no list/status/tag audience — leftover filters from a
    // previous lists-mode config must not survive, or the scheduler would
    // skip every contact the trigger just enqueued.
    return {
      ...pool,
      importName: API_TRIGGER,
      importNames: [API_TRIGGER],
      statusFilter: '',
      tagFilter: '',
    };
  }
  if (next.mode === 'manual') {
    return { ...pool, importName: MANUAL_IMPORT, importNames: [MANUAL_IMPORT] };
  }
  if (next.mode === 'any') {
    return { ...pool, importName: '', importNames: [] };
  }
  const names = cleanNames(next.names);
  if (names.length === 0) {
    return { ...pool, importName: '', importNames: [] };
  }
  return {
    ...pool,
    importName: names[0],
    importNames: names,
  };
};

export const togglePoolList = (pool, name) => {
  if (typeof name !== 'string' || !name.trim() || name === MANUAL_IMPORT || name === API_TRIGGER) {
    return applyPoolLists(pool, { mode: 'any' });
  }
  const { mode, names } = resolvePoolLists(pool);
  if (mode !== 'lists') {
    return applyPoolLists(pool, { mode: 'lists', names: [name] });
  }
  const next = names.includes(name)
    ? names.filter((n) => n !== name)
    : [...names, name];
  return applyPoolLists(pool, next.length ? { mode: 'lists', names: next } : { mode: 'any' });
};

/** Round-robin flatten so daily volume is shared across selected lists. */
export const interleaveGroups = (groups = []) => {
  const queues = groups.map((g) => (Array.isArray(g) ? [...g] : []));
  const out = [];
  let added = true;
  while (added) {
    added = false;
    for (const q of queues) {
      if (q.length) {
        out.push(q.shift());
        added = true;
      }
    }
  }
  return out;
};

const WEEKEND = new Set(['Sat', 'Sun']);

export const isWeekendMs = (ms, timezone = 'Europe/Berlin') => {
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
  }).format(new Date(ms));
  return WEEKEND.has(day);
};

export const stepWorkingDay = (ms, timezone = 'Europe/Berlin', skipWeekends = true) => {
  let t = ms + 86400000;
  if (skipWeekends) {
    for (let i = 0; i < 8 && isWeekendMs(t, timezone); i += 1) t += 86400000;
  }
  return t;
};

/** YYYY-MM-DD in the job timezone — prepare / research budgets key off this. */
export const getLocalDayKey = (timezone = 'Europe/Berlin', fromMs = Date.now()) => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone || 'Europe/Berlin' })
      .format(new Date(Number(fromMs) || Date.now()));
  } catch {
    return new Date(Number(fromMs) || Date.now()).toISOString().slice(0, 10);
  }
};

// 00:05 local — so a draft is already being written when the calendar day
// flips, not +24h after whoever activated the job.
const PREPARE_DAY_START_MINUTES = 5;

const tzWallParts = (ms, timezone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const hourRaw = parseInt(get('hour'), 10);
  return {
    year: parseInt(get('year'), 10),
    month: parseInt(get('month'), 10),
    day: parseInt(get('day'), 10),
    hour: hourRaw === 24 ? 0 : hourRaw,
    minute: parseInt(get('minute'), 10),
  };
};

/** Instant of a civil wall time in `timezone` (DST-safe, host-TZ independent). */
const zonedLocalMs = (year, month, day, hour, minute, timezone) => {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const got = tzWallParts(utcGuess, timezone);
  const gotAsUtc = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute, 0);
  return utcGuess - (gotAsUtc - utcGuess);
};

/**
 * Next local calendar day at 00:05 in `timezone`, strictly after `fromMs`.
 * Prepare jobs pace later days here so overnight generation fills the strip
 * before anyone opens the inbox.
 */
export const nextLocalDayStart = (fromMs, timezone = 'Europe/Berlin') => {
  const tz = timezone || 'Europe/Berlin';
  const from = Number(fromMs);
  const origin = Number.isFinite(from) ? from : Date.now();
  try {
    const local = tzWallParts(origin, tz);
    const next = new Date(Date.UTC(local.year, local.month - 1, local.day));
    next.setUTCDate(next.getUTCDate() + 1);
    return zonedLocalMs(
      next.getUTCFullYear(),
      next.getUTCMonth() + 1,
      next.getUTCDate(),
      0,
      PREPARE_DAY_START_MINUTES,
      tz,
    );
  } catch {
    return origin + 86400000;
  }
};

/**
 * Only `dailyLimit` interleaved contacts become due each working day.
 * 30/day + 3 lists → first 30 timestamps are today (10/10/10), next 30 tomorrow.
 *
 * `alignNextDays: 'localMidnight'` (prepare jobs) puts day 2+ at local 00:05
 * so the agent starts writing as soon as the day opens. Sending jobs keep
 * the +24h offset so they stay aligned with the current send window.
 */
export const pacedExecuteAts = (count, opts = {}) => {
  const {
    dailyLimit = 30,
    startMs = Date.now(),
    skipWeekends = true,
    timezone = 'Europe/Berlin',
    alignNextDays = 'offset',
  } = opts;
  const n = Math.max(0, Number(count) || 0);
  const limit = Math.max(1, Number(dailyLimit) || 30);
  if (n === 0) return [];
  const stepNext = (ms) => (
    alignNextDays === 'localMidnight'
      ? nextLocalDayStart(ms, timezone)
      : stepWorkingDay(ms, timezone, skipWeekends)
  );
  let dayStart = startMs;
  if (skipWeekends && isWeekendMs(dayStart, timezone)) {
    dayStart = stepWorkingDay(dayStart, timezone, true);
  }
  const daysNeeded = Math.ceil(n / limit);
  const starts = [];
  for (let d = 0; d < daysNeeded; d += 1) {
    if (d > 0) dayStart = stepNext(dayStart);
    starts.push(dayStart);
  }
  return Array.from({ length: n }, (_, i) => starts[Math.floor(i / limit)]);
};

/** Names in `extraNames` that this pool does not already target. */
export const listsNotOnPool = (pool, extraNames) => {
  const current = new Set(resolvePoolLists(pool).names);
  return cleanNames(extraNames).filter((name) => !current.has(name));
};

/**
 * Add lists to a lists-mode pool. Status, tag and sort stay.
 * Any, manual and API pools are not a fixed list set, so they stay as they are.
 */
export const appendPoolLists = (pool = {}, extraNames = []) => {
  const { mode, names } = resolvePoolLists(pool);
  if (mode !== 'lists') return pool;
  const extras = listsNotOnPool(pool, extraNames);
  if (extras.length === 0) return pool;
  return applyPoolLists(pool, { mode: 'lists', names: [...names, ...extras] });
};

/**
 * Where a batch added onto a running agent should start.
 * No queue yet → start now. The last scheduled day still has free slots →
 * fill those. That day is already full → the next working day.
 */
export const paceStartAfterBacklog = (executeAts, now, opts = {}) => {
  const limit = Math.max(1, Number(opts.dailyLimit) || 30);
  const origin = Number(now) || Date.now();
  const times = (executeAts || []).map((t) => Number(t)).filter((t) => Number.isFinite(t));
  if (times.length === 0) return { startMs: origin, slotsTaken: 0 };
  const last = Math.max(...times);
  const onLast = times.filter((t) => t === last).length;
  if (onLast < limit) return { startMs: last, slotsTaken: onLast };
  const timezone = opts.timezone || 'Europe/Berlin';
  const skipWeekends = opts.skipWeekends !== false;
  const startMs = opts.alignNextDays === 'localMidnight'
    ? nextLocalDayStart(last, timezone)
    : stepWorkingDay(last, timezone, skipWeekends);
  return { startMs, slotsTaken: 0 };
};

const TERMINAL_QUEUE = new Set(['completed', 'replied', 'failed', 'skipped']);

/**
 * Who must not be queued again, and which timestamps the new intros line up behind.
 * Follow-ups stay out of that line: their executeAt is a later step, not a free
 * intro slot. Anyone this agent already holds or already finished stays out,
 * including a manual remove that never wrote outreach history.
 */
export const queueGuardForAddedLists = (queueEntries, archiveEntries) => {
  const blocked = new Set();
  const backlogAts = [];
  (queueEntries || []).forEach((entry) => {
    if (!entry || entry.isTestRun) return;
    const cid = entry.contactId || '';
    if (entry.sameJob) {
      if (cid) blocked.add(cid);
      const step = Number(entry.currentStepIndex) || 0;
      if (!TERMINAL_QUEUE.has(entry.status) && step === 0 && entry.executeAt != null) {
        backlogAts.push(Number(entry.executeAt));
      }
      return;
    }
    if (!TERMINAL_QUEUE.has(entry.status) && cid) blocked.add(cid);
  });
  (archiveEntries || []).forEach((entry) => {
    if (!entry || entry.isTestRun || !entry.contactId) return;
    blocked.add(entry.contactId);
  });
  return { blocked, backlogAts };
};

/** Paced timestamps for contacts added behind the ones already queued. */
export const executeAtsAfterBacklog = (count, backlogAts, paceOpts = {}) => {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return [];
  const { startMs, slotsTaken } = paceStartAfterBacklog(backlogAts, paceOpts.startMs, paceOpts);
  return pacedExecuteAts(slotsTaken + n, { ...paceOpts, startMs }).slice(slotsTaken);
};

/** How many contacts each list should contribute to today's quota. */
export const takeDailyShares = (groupSizes = [], dailyLimit = 30) => {
  const sizes = groupSizes.map((n) => Math.max(0, Number(n) || 0));
  const taken = sizes.map(() => 0);
  let left = Math.max(1, Number(dailyLimit) || 30);
  let progressed = true;
  while (left > 0 && progressed) {
    progressed = false;
    for (let i = 0; i < sizes.length && left > 0; i += 1) {
      if (taken[i] < sizes[i]) {
        taken[i] += 1;
        left -= 1;
        progressed = true;
      }
    }
  }
  return taken;
};

export const isManualPool = (pool) => resolvePoolLists(pool).mode === 'manual';
export const isAnyListPool = (pool) => resolvePoolLists(pool).mode === 'any';
export const isApiPool = (pool) => resolvePoolLists(pool).mode === 'api';

export const contactMatchesPoolLists = (contact, pool) => {
  const { mode, names } = resolvePoolLists(pool);
  // Manual + api pools bypass the list filter: entries are either added from
  // the table or enqueued one-by-one by the API trigger, so the list of
  // contacts they can accept is not driven by `import_name`.
  if (mode === 'any' || mode === 'manual' || mode === 'api') return true;
  return names.includes(contact?.import_name);
};

export const poolListsLabel = (pool) => {
  const { mode, names } = resolvePoolLists(pool);
  if (mode === 'api') return 'API trigger — pushed by webhook';
  if (mode === 'manual') return 'Manual — from the table';
  if (mode === 'any') return 'Any list';
  if (names.length === 1) return names[0];
  return `${names.length} lists · split evenly`;
};

export const poolListsHint = (pool) => {
  const { mode, names } = resolvePoolLists(pool);
  if (mode === 'api') return 'Contacts are enqueued by external systems via the API trigger endpoint.';
  if (mode === 'manual') return 'Add contacts from the table after you activate the job.';
  if (mode === 'any') return 'Every import. Combined with Status and Tag.';
  if (names.length === 1) return 'Only contacts from this import.';
  return `Each day the send/prepare quota is split evenly across these ${names.length} lists.`;
};

export const describePoolLists = (pool) => {
  const { mode, names } = resolvePoolLists(pool);
  if (mode === 'api') return 'API trigger';
  if (mode === 'manual') return 'Manual (from table)';
  if (mode === 'any') return '';
  if (names.length === 1) return names[0];
  return names.join(' · ');
};

/** Import filenames → a chip label: drop .csv, turn underscores into spaces. */
export const prettyImportName = (name) => String(name || '')
  .replace(/\.csv$/i, '')
  .replace(/[_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
